import { createClient } from "@supabase/supabase-js";
import { getBingoResolutionFromDataList } from "./BingoResolution";

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL || "url";
const supabaseKey = process.env.REACT_APP_SUPABASE_KEY || "xyz";
const supabase = createClient(supabaseUrl, supabaseKey);

export const fetchBingoResolutions = async (user: string, year: string) => {
  const { data, error } = await supabase
    .from("bingoResolutions")
    .select("*")
    .eq("user", user)
    .eq("year", year)
    .order("id", { ascending: true });

  return { data: getBingoResolutionFromDataList(data || []), error };
};

export const fetchValidBingoUsers = async (year: string) => {
  const { data, error } = await supabase
    .from("bingoResolutions")
    .select("user")
    .eq("year", year);

  // Build a Set of unique users from the returned rows, then return as an array
  const userSet = new Set<string>();
  if (data && Array.isArray(data)) {
    for (const row of data) {
      // row may be e.g. { user: 'liam' }
      if (row && (row as any).user) {
        userSet.add(String((row as any).user));
      }
    }
  }

  const users = Array.from(userSet).sort((a, b) => a.localeCompare(b));
  return { data: users, error };
};

export interface UpdateBingoResolutionTallyRequest {
  resolutionId: string;
  newTally: number;
  locationData: any;
}

export const updateBingoResolutionTally = async (
  request: UpdateBingoResolutionTallyRequest,
) => {
  const { data: loggingData, error: loggingError } = await supabase
    .from("bingoResolutionUpdates")
    .insert([
      {
        resolution_id: request.resolutionId,
        ip_address: request.locationData?.ip || null,
        city: request.locationData?.city || null,
        region: request.locationData?.region || null,
        country: request.locationData?.country || null,
        change: `newTally: "${request.newTally}"`,
      },
    ]);
  if (loggingError) {
    console.error(
      `Error logging bingo resolution update for ${request.resolutionId}: ${loggingError} for data: ${loggingData}`,
    );
  }

  const { data, error } = await supabase
    .from("bingoResolutions")
    .update({ current_tally: request.newTally, updated_at: new Date() })
    .eq("id", request.resolutionId);
  return { data, error };
};

export interface UpdateBingoResolutionRequest {
  resolutionId: string;
  newTitle: string;
  newGoalTally: number;
  newCurrentTally: number;
  newColor: string;
  newNote: string;
  locationData: any;
}

export const updateBingoResolution = async (
  request: UpdateBingoResolutionRequest,
) => {
  const { data: loggingData, error: loggingError } = await supabase
    .from("bingoResolutionUpdates")
    .insert([
      {
        resolution_id: request.resolutionId,
        ip_address: request.locationData?.ip || null,
        city: request.locationData?.city || null,
        region: request.locationData?.region || null,
        country: request.locationData?.country || null,
        change: `title: "${request.newTitle}", current_tally: ${request.newCurrentTally}, goal_tally:${request.newGoalTally}, color: "${request.newColor}", note: "${request.newNote}"`,
      },
    ]);
  if (loggingError) {
    console.error(
      `Error logging bingo resolution update for ${request.resolutionId}: ${loggingError} for data: ${loggingData}`,
    );
  }

  const { data, error } = await supabase
    .from("bingoResolutions")
    .update({
      title: request.newTitle,
      goal_tally: request.newGoalTally,
      current_tally: request.newCurrentTally,
      color: request.newColor,
      note: request.newNote,
      updated_at: new Date(),
    })
    .eq("id", request.resolutionId);
  return { data, error };
};

export interface CreateBingoResolutionsForNewUserReqyest {
  user: string;
  year: string;
}

export const createBingoResolutionsForNewUser = async (
  request: CreateBingoResolutionsForNewUserReqyest,
) => {
  const defaultResolutions = [];
  for (let i = 0; i < 25; i++) {
    if (i === 12) {
      defaultResolutions.push({ title: `Turn _!` });
    } else {
      defaultResolutions.push({ title: "" });
    }
  }

  const rowsToInsert = defaultResolutions.map((resolution) => ({
    user: request.user,
    year: request.year,
    title: resolution.title,
    goal_tally: 1,
    current_tally: 0,
  }));

  const { data, error } = await supabase
    .from("bingoResolutions")
    .insert(rowsToInsert);

  if (error) {
    console.error(
      `Error creating bingo resolutions for new user ${request.user} for year ${request.year}: ${error.message}`,
    );
  }

  return { data, error };
};
